import AccountTreeIcon from '@mui/icons-material/AccountTree'
import HistoryIcon from '@mui/icons-material/History'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import CMSHeader from 'components/CMSHeader'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStoreActions, useStoreState } from 'store/hooks'
import { fetchData } from 'utils/fetchData'
import { LOAD_PAGE } from 'graphql/queries/loadPage'
import { Query } from 'graphql/types'
import { NavigationRoutes } from 'utils/navigationRoutes'
import { EditingDataType, PagePreviewCommandType } from 'utils/types'
import { getParsedPageElements } from 'utils/parseElementContent'
import { AILiveElementTree } from './AILiveElementTree'
import { AILiveHistory } from './AILiveHistory'
import { useAILive } from './useAILive'

export function AILivePage(): JSX.Element {
    const navigate = useNavigate()
    const unpublishedItems = useStoreState((state) => state.model.unpublishedItems)
    const websiteList = useStoreState((state) => state.model.websiteList)
    const selectedWebsite = useStoreState((state) => state.model.selectedWebsite)
    const setSelectedWebsite = useStoreActions((actions) => actions.model.setSelectedWebsite)
    const startEditingPage = useStoreActions((actions) => actions.model.startEditingPage)
    const pageList = useStoreState((state) => state.model.pageList)
    const storeAssets = useStoreState((state) => state.model.assetsSimplified)
    const storeBlocks = useStoreState((state) => state.model.blockData)

    const { events, currentEvent, scrollCommand, clearScrollCommand, connected } = useAILive()

    const [showTree, setShowTree] = useState(true)
    const [showHistory, setShowHistory] = useState(true)
    const [currentContents, setCurrentContents] = useState<string>('[]')
    const [currentPageTitle, setCurrentPageTitle] = useState<string>('')
    const [currentPageID, setCurrentPageID] = useState<string>('')
    const iframeRef = useRef<HTMLIFrameElement>(null)
    // Keep a ref to the latest page content so we can resend on previewReady
    const latestPageContent = useRef<any[]>([])

    const previewURL = selectedWebsite?.previewURL ?? ''

    // Send content to iframe using the same protocol as PagePreview component
    const sendToIframe = useCallback((messageType: string, payload: any) => {
        if (!iframeRef.current?.contentWindow) return
        iframeRef.current.contentWindow.postMessage(JSON.stringify({ type: messageType, payload }), '*')
    }, [])

    const scrollToElement = useCallback(
        (elementID: string) => {
            sendToIframe('scrollToElement', { elementId: elementID })
        },
        [sendToIframe],
    )

    const sendPageToIframe = useCallback(
        (pageContent: any[]) => {
            latestPageContent.current = pageContent
            sendToIframe(PagePreviewCommandType.UPDATE_CONTENT, {
                pageContent,
                blocks: storeBlocks ?? [],
                assets: storeAssets ?? [],
            })
            setTimeout(() => {
                sendToIframe(PagePreviewCommandType.REFRESH_ELEMENT_OVERLAYS, {})
                sendToIframe(PagePreviewCommandType.SET_EDIT_HELPERS, { visible: false })
                sendToIframe(PagePreviewCommandType.SET_OVERLAY, { enabled: true })
            }, 200)
        },
        [sendToIframe, storeAssets, storeBlocks],
    )

    // Listen for previewReady from iframe — resend content when iframe is ready
    useEffect(() => {
        if (!selectedWebsite) return
        const handler = (message: MessageEvent) => {
            try {
                if (typeof message.data !== 'string' || !message.data.startsWith('{')) return
                const data = JSON.parse(message.data)
                if (data.type === PagePreviewCommandType.PREVIEW_READY) {
                    // Resend current content if we have any
                    if (latestPageContent.current.length > 0) {
                        sendPageToIframe(latestPageContent.current)
                    }
                    sendToIframe(PagePreviewCommandType.SET_EDIT_HELPERS, { visible: false })
                    sendToIframe(PagePreviewCommandType.SET_OVERLAY, { enabled: true })
                }
            } catch {
                // ignore non-JSON messages
            }
        }
        window.addEventListener('message', handler)
        return () => window.removeEventListener('message', handler)
    }, [selectedWebsite, sendToIframe, sendPageToIframe])

    // Fetch page content and send to iframe
    const loadAndDisplayPage = useCallback(
        async (pageID: string) => {
            const result = await fetchData<Query>(LOAD_PAGE, { pageID })
            if (!result?.data?.loadPage) return

            const page = result.data.loadPage
            const contentsStr = page.currentVersion.contents
            setCurrentPageTitle(page.currentVersion.pageSettings.htmlTitle)
            setCurrentContents(contentsStr)
            setCurrentPageID(pageID)

            try {
                const pageContent = JSON.parse(contentsStr || '[]')
                sendPageToIframe(pageContent)
            } catch {
                // ignore parse errors
            }
        },
        [sendPageToIframe],
    )

    // When any page event arrives, fetch and display that page
    useEffect(() => {
        if (!currentEvent?.pageID) return
        loadAndDisplayPage(currentEvent.pageID)
    }, [currentEvent, loadAndDisplayPage])

    // When AI requests a scroll-to-element, load the page if needed then scroll
    useEffect(() => {
        if (!scrollCommand) return
        const { pageID, elementID } = scrollCommand
        clearScrollCommand()

        if (pageID === currentPageID) {
            // Same page — scroll directly
            scrollToElement(elementID)
        } else {
            // Different page — load it first, then scroll after content is sent to iframe
            loadAndDisplayPage(pageID).then(() => {
                setTimeout(() => scrollToElement(elementID), 300)
            })
        }
    }, [scrollCommand]) // eslint-disable-line react-hooks/exhaustive-deps

    // On mount, load the last page the MCP touched (persisted by global WebSocket)
    useEffect(() => {
        const lastPageID = sessionStorage.getItem('mcpLastPageID')
        if (lastPageID && !currentPageID) {
            loadAndDisplayPage(lastPageID)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Edit: open the current page in the CMS editor
    const handleEditPage = useCallback(() => {
        if (!currentPageID) return
        const page = pageList.find((p) => p.id === currentPageID)
        if (!page) return
        // Remember where to go back to after save/cancel
        sessionStorage.setItem('pageEditReturnTo', NavigationRoutes.AI_LIVE)
        startEditingPage({
            currentlyEditingData: {
                id: page.id,
                identifier: '',
                label: page.currentVersion.pageSettings.htmlTitle,
                contents: page.currentVersion.contents,
                type: EditingDataType.PAGE,
            },
            pageElementContents: getParsedPageElements(page.currentVersion.contents),
        })
        navigate('/PageEdit')
    }, [currentPageID, pageList, startEditingPage, navigate])

    // Refresh: reload current page
    const handleRefresh = () => {
        if (currentPageID) {
            loadAndDisplayPage(currentPageID)
        } else {
            const lastPageID = sessionStorage.getItem('mcpLastPageID')
            if (lastPageID) loadAndDisplayPage(lastPageID)
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <CMSHeader
                unpublishedItems={unpublishedItems}
                selectedWebsite={selectedWebsite?.id ?? ''}
                websiteList={websiteList}
                onSelectWebsite={setSelectedWebsite}
            />

            {/* Status bar */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                }}
            >
                <Chip
                    label={connected ? 'Connected' : 'Disconnected'}
                    color={connected ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                />
                {currentPageTitle && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        Viewing: <strong>{currentPageTitle}</strong>
                    </Typography>
                )}
                <Tooltip title="Refresh current page">
                    <IconButton size="small" onClick={handleRefresh}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Toggle element tree">
                    <IconButton
                        size="small"
                        onClick={() => setShowTree((v) => !v)}
                        color={showTree ? 'primary' : 'default'}
                    >
                        <AccountTreeIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Toggle session history">
                    <IconButton
                        size="small"
                        onClick={() => setShowHistory((v) => !v)}
                        color={showHistory ? 'primary' : 'default'}
                    >
                        <HistoryIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Main content area */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Left panel: Element tree */}
                {showTree && (
                    <Paper
                        elevation={0}
                        sx={{
                            width: 320,
                            minWidth: 320,
                            borderRight: '1px solid #e0e0e0',
                            overflowY: 'auto',
                            flexShrink: 0,
                        }}
                    >
                        <AILiveElementTree
                            contents={currentContents}
                            pageID={currentPageID}
                            onEditPage={handleEditPage}
                            onElementClick={scrollToElement}
                        />
                    </Paper>
                )}

                {/* Center: iframe preview */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch', overflow: 'hidden' }}>
                    {previewURL ? (
                        <iframe
                            ref={iframeRef}
                            src={previewURL}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title="AI Live Preview"
                        />
                    ) : (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography variant="body1" color="text.secondary">
                                No preview URL configured
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Right panel: History */}
                {showHistory && (
                    <Paper
                        elevation={0}
                        sx={{
                            width: 320,
                            minWidth: 320,
                            borderLeft: '1px solid #e0e0e0',
                            overflowY: 'auto',
                            flexShrink: 0,
                        }}
                    >
                        <AILiveHistory events={events} />
                    </Paper>
                )}
            </Box>
        </Box>
    )
}
